import React, { useEffect } from 'react';
import Disclaimer from '../components/Disclaimer';

// Long-form methodology page (FR-039). Defensibility surface — explains
// the valuation pipeline at the level of abstraction in the customer-facing
// overview document (no formula box, no exposed tier-ladder $ figures,
// no driver-level breakdown).

export default function Methodology() {
  // Scroll to the top of the page on mount so navigating in from the
  // wizard's Estimate screen (which is typically scrolled deep) doesn't
  // land the user mid-page.
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, []);

  return (
    <div style={pageStyle}>
      <header style={heroStyle}>
        <h1 style={titleStyle}>Healthcare Data Valuation Engine — Overview</h1>
      </header>

      <Section eyebrow="What it does">
        <p style={proseStyle}>
          Our platform estimates the commercial value of healthcare and biobank datasets and the cost
          of licensing them. It serves both data suppliers (biobanks, academic medical centers,
          CROs) seeking to understand and price their assets, and data buyers (pharma, diagnostics,
          and AI developers) scoping the cost of a specific license.
        </p>
        <p style={proseStyle}>
          The engine translates the qualitative and quantitative characteristics of a dataset into
          defensible value estimates and partnership economics, all grounded in real-world market
          benchmarks.
        </p>
      </Section>

      <Section eyebrow="How it works (at a high level)">
        <p style={proseStyle}>The valuation process moves through four conceptual stages:</p>
        <ol style={stageListStyle}>
          <li style={stageItemStyle}>
            <div style={stageNumStyle}>1</div>
            <div>
              <div style={stageNameStyle}>Multi-dimensional quality assessment.</div>
              <p style={stageBodyStyle}>
                Each dataset is profiled across several core dimensions of scientific and commercial
                value — including data rarity, quality, research usefulness, regulatory and consent
                posture, and the richness of linked records. These dimensions are combined using a
                proprietary scoring model that rewards well-rounded, research-ready assets and
                reflects how weaknesses in any single dimension affect overall desirability.
              </p>
            </div>
          </li>
          <li style={stageItemStyle}>
            <div style={stageNumStyle}>2</div>
            <div>
              <div style={stageNameStyle}>Contextual adjustments.</div>
              <p style={stageBodyStyle}>
                The base profile is refined using additional characteristics of the asset, such as
                cohort scale, the breadth and combination of data modalities (e.g., genomic,
                imaging, clinical, multi-omic), population diversity, longitudinal follow-up, data
                recency, and consent permissions. Consent and data-use rights are evaluated using a
                structured framework aligned with an open international standard.
              </p>
            </div>
          </li>
          <li style={stageItemStyle}>
            <div style={stageNumStyle}>3</div>
            <div>
              <div style={stageNameStyle}>Market-calibrated pricing.</div>
              <p style={stageBodyStyle}>
                The resulting profile is mapped to value bands and pricing structures that have been
                calibrated against a curated set of validated, publicly reported reference
                transactions and a market sizing analysis for the biobank/health-data sector. The
                model produces indicative figures for pharma partnership value, per-participant
                economics, and recurring AI/ML licensing — and adjusts for common deal structures
                such as exclusivity, multi-year terms, and ongoing data enrichment.
              </p>
            </div>
          </li>
          <li style={stageItemStyle}>
            <div style={stageNumStyle}>4</div>
            <div>
              <div style={stageNameStyle}>Benchmarking and guidance.</div>
              <p style={stageBodyStyle}>
                Outputs are compared against disease-area and per-participant benchmarks to give a
                clear read on where an estimate sits relative to the market, along with practical,
                actionable guidance on how a dataset's value could be strengthened.
              </p>
            </div>
          </li>
        </ol>
      </Section>

      <Section eyebrow="Dataset inventory and packaging">
        <p style={proseStyle}>
          For suppliers, the platform also estimates how many distinct, listable data products an
          asset can support. It considers the combinations of cohorts and data modalities available
          and how those assets can be packaged for high-value research use cases (such as biomarker
          discovery, AI/ML development and validation, external control arms, and real-world
          evidence studies), applying minimum-viability and suitability rules so that only credible
          products are surfaced.
        </p>
      </Section>

      <Section eyebrow="What makes it credible">
        <ul style={credListStyle}>
          <li>
            <strong>Grounded in real transactions.</strong> Pricing logic is anchored to validated
            reference deals and market analysis rather than guesswork.
          </li>
          <li>
            <strong>Standards-aligned.</strong> Consent and data-use evaluation follows the open
            GA4GH DUO framework.
          </li>
          <li>
            <strong>Transparent and explainable.</strong> Every estimate is accompanied by the
            factors that drove it and by improvement guidance.
          </li>
          <li>
            <strong>Indicative by design.</strong> Results are decision-support estimates, not
            quotes; they are intended to inform negotiations, not replace them.
          </li>
        </ul>
      </Section>

      <div style={disclaimerWrapStyle}>
        <Disclaimer />
      </div>
    </div>
  );
}

function Section({ eyebrow, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionEyebrowStyle}>{eyebrow}</div>
      {children}
    </section>
  );
}

// styles
const pageStyle = { padding: '40px 56px 64px', maxWidth: '960px', margin: '0 auto', fontFamily: 'var(--font)', color: 'var(--ink)' };

const heroStyle = { marginBottom: '44px' };
const titleStyle = { fontFamily: 'var(--display)', fontWeight: 500, fontSize: '42px', letterSpacing: '-0.02em', lineHeight: 1.05, margin: 0, color: 'var(--ink)' };

const sectionStyle = { marginBottom: '44px' };
const sectionEyebrowStyle = { fontSize: '11px', color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' };

const proseStyle = { fontSize: '14.5px', color: 'var(--ink2)', lineHeight: 1.65, margin: '0 0 14px' };

const stageListStyle = { listStyle: 'none', padding: 0, margin: '14px 0', display: 'flex', flexDirection: 'column', gap: '16px' };
const stageItemStyle = { display: 'grid', gridTemplateColumns: '40px 1fr', gap: '16px', padding: '18px 20px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '6px', alignItems: 'start' };
const stageNumStyle = {
  fontFamily: 'var(--display)',
  fontStyle: 'italic',
  fontWeight: 500,
  fontSize: '28px',
  lineHeight: 1,
  color: 'var(--accent)',
  textAlign: 'center',
  paddingTop: '2px',
};
const stageNameStyle = { fontSize: '15px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' };
const stageBodyStyle = { fontSize: '13.5px', color: 'var(--ink2)', lineHeight: 1.6, margin: 0 };

const credListStyle = { margin: '0 0 14px 0', paddingLeft: '20px', color: 'var(--ink2)', fontSize: '14.5px', lineHeight: 1.7 };

const disclaimerWrapStyle = { marginTop: '40px', paddingTop: '24px', borderTop: '1px solid var(--line)' };
